import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { CATEGORIES } from '@/types/places';
import { useMapStore } from '@/store/mapStore';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Check, RotateCcw } from 'lucide-react';
import { KOSHER_FILTER_OPTIONS, KOSHER_AUTHORITIES } from '@/lib/kosherLabels';
import { useKosherAuthorityCounts } from '@/hooks/useKosherAuthorityCounts';

interface FilterSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

export const FilterSheet = ({ isOpen, onClose }: FilterSheetProps) => {
  const { 
    activeCategories, 
    toggleCategory, 
    activeKosherStatuses, 
    toggleKosherStatus,
    activeKosherAuthorities,
    toggleKosherAuthority,
    resetFilters 
  } = useMapStore();

  const { data: authorityCounts } = useKosherAuthorityCounts();

  const handleReset = () => {
    resetFilters();
  };

  const hasActiveFilters = activeCategories.length > 0 || activeKosherStatuses.length > 0 || activeKosherAuthorities.length > 0;
  const activeFiltersCount = activeCategories.length + activeKosherStatuses.length + activeKosherAuthorities.length;

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="bottom" className="rounded-t-3xl max-h-[85vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            Filtrer les lieux
            {activeFiltersCount > 0 && (
              <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                {activeFiltersCount} actif{activeFiltersCount > 1 ? 's' : ''}
              </span>
            )}
          </SheetTitle>
        </SheetHeader>
        
        <div className="py-6 space-y-6">
          {/* Categories - Multi-select */}
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-3">
              Catégories
              {activeCategories.length > 0 && (
                <span className="ml-2 text-xs bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full">
                  {activeCategories.length}
                </span>
              )}
            </h3>
            <p className="text-xs text-muted-foreground mb-3">Sélectionnez plusieurs catégories pour élargir la recherche</p>
            <div className="grid grid-cols-2 gap-2">
              {CATEGORIES.map((category) => {
                const isActive = activeCategories.includes(category.id);
                return (
                  <button
                    key={category.id}
                    onClick={() => toggleCategory(category.id)}
                    className={cn(
                      'p-3 rounded-xl border text-left transition-all',
                      isActive 
                        ? 'border-primary bg-primary/10 text-primary' 
                        : 'border-border hover:border-primary/50'
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span>{category.icon}</span>
                        <span className="font-medium">{category.name}</span>
                      </div>
                      {isActive && <Check className="w-4 h-4" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Kosher Status - Multi-select */}
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-3">
              Type de cacherout
              {activeKosherStatuses.length > 0 && (
                <span className="ml-2 text-xs bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full">
                  {activeKosherStatuses.length}
                </span>
              )}
            </h3>
            <p className="text-xs text-muted-foreground mb-3">Sélectionnez plusieurs options pour élargir la recherche</p>
            <div className="grid grid-cols-2 gap-2">
              {KOSHER_FILTER_OPTIONS.map((option) => {
                const isActive = activeKosherStatuses.includes(option.value);
                return (
                  <button
                    key={option.value}
                    onClick={() => toggleKosherStatus(option.value)}
                    className={cn(
                      'p-3 rounded-xl border text-left transition-all',
                      isActive 
                        ? 'border-primary bg-primary/10 text-primary' 
                        : 'border-border hover:border-primary/50'
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{option.label}</span>
                      {isActive && <Check className="w-4 h-4" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Kosher Authority - Multi-select */}
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-3">
              Autorité rabbinique
              {activeKosherAuthorities.length > 0 && (
                <span className="ml-2 text-xs bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full">
                  {activeKosherAuthorities.length}
                </span>
              )}
            </h3>
            <p className="text-xs text-muted-foreground mb-3">Sélectionnez plusieurs autorités</p>
            <div className="grid grid-cols-2 gap-2">
              {KOSHER_AUTHORITIES.map((authority) => {
                const isActive = activeKosherAuthorities.includes(authority.value);
                return (
                  <button
                    key={authority.value}
                    onClick={() => toggleKosherAuthority(authority.value)}
                    className={cn(
                      'p-3 rounded-xl border text-left transition-all',
                      isActive 
                        ? 'border-primary bg-primary/10 text-primary' 
                        : 'border-border hover:border-primary/50'
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{authority.label}</span>
                      <div className="flex items-center gap-1.5">
                        {authorityCounts?.[authority.value] !== undefined && authorityCounts[authority.value] > 0 && (
                          <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">
                            {authorityCounts[authority.value]}
                          </span>
                        )}
                        {isActive && <Check className="w-4 h-4" />}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3">
            {hasActiveFilters && (
              <Button 
                variant="outline"
                onClick={handleReset} 
                className="flex-1"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Réinitialiser
              </Button>
            )}
            <Button 
              onClick={onClose} 
              className={cn(
                "gradient-primary text-primary-foreground",
                hasActiveFilters ? "flex-1" : "w-full"
              )}
            >
              Appliquer les filtres
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
